import { useEffect } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  fetchWebsiteServices,
  fetchWebsiteService,
  fetchWebsiteBlogs,
  fetchWebsiteBlog,
  fetchWebsiteBlogMeta,
  fetchWebsiteContact,
  fetchWebsiteGallery,
  fetchWebsiteTestimonials,
  fetchFrontendMode,
  fetchWebsitePlans,
  fetchPublicSettings,
  submitContactForm,
  type WebsiteContactInfo,
  type ContactFormPayload,
  type WebsiteBlogQuery,
  type GalleryAsset,
  type PublicSettingsMap,
} from '@/services/httpServices/websiteService'
import { services as staticServices, getService } from '@/data/services'
import {
  posts as staticPosts,
  getPost,
  selectPosts,
  blogCategoryTree,
  blogTagTree,
} from '@/data/blog'
import { galleryImages, galleryVideos } from '@/data/gallery'
import { testimonials as fallbackTestimonials } from '@/data/testimonials'
import { plans as fallbackPlans } from '@/data/plans'
import { site } from '@/data/site'

const STALE_TIME = 5 * 60 * 1000

/** Contact details with the bundled defaults as an offline fallback. */
export const defaultContact: WebsiteContactInfo = {
  phone: site.phone,
  phoneAlt: site.phoneAlt,
  email: site.email,
  address: site.address,
  workingHours: site.hours,
  mapEmbedUrl: site.mapEmbedUrl,
  mapLinkUrl: site.mapLinkUrl,
  isActive: true,
}

/** Website services, falling back to the bundled demo content when offline. */
export function useWebsiteServices() {
  const query = useQuery({
    queryKey: ['website', 'services'],
    queryFn: fetchWebsiteServices,
    staleTime: STALE_TIME,
  })

  return {
    services: query.data?.data ?? staticServices,
    isLoading: query.isLoading,
    isFallback: !!query.error,
  }
}

/** A single service by slug, falling back to the bundled demo content. */
export function useWebsiteService(slug?: string) {
  const query = useQuery({
    queryKey: ['website', 'service', slug],
    queryFn: () => fetchWebsiteService(slug!),
    enabled: !!slug,
    staleTime: STALE_TIME,
  })

  return {
    service: query.data?.data ?? getService(slug),
    isLoading: query.isLoading,
    isFallback: !!query.error,
  }
}

/**
 * Published blog posts, with optional server-side multi-category/tag/search
 * filters. Falls back to the bundled demo content (filtered the same way).
 */
export function useWebsiteBlogs(filters: WebsiteBlogQuery = {}) {
  const { categories, tags, search } = filters
  const categoryKey = (categories ?? []).join(',')
  const tagKey = (tags ?? []).join(',')
  const query = useQuery({
    queryKey: ['website', 'blogs', { categories: categoryKey, tags: tagKey, search: search ?? null }],
    queryFn: () => fetchWebsiteBlogs({ categories, tags, search }),
    staleTime: STALE_TIME,
  })

  return {
    posts: query.data?.data ?? selectPosts(staticPosts, { categories, tags, search }),
    isLoading: query.isLoading,
    isFallback: !!query.error,
  }
}

/** A single blog post by slug, falling back to the bundled demo content. */
export function useWebsiteBlog(slug?: string) {
  const query = useQuery({
    queryKey: ['website', 'blog', slug],
    queryFn: () => fetchWebsiteBlog(slug!),
    enabled: !!slug,
    staleTime: STALE_TIME,
  })

  return {
    post: query.data?.data ?? getPost(slug),
    isLoading: query.isLoading,
    isFallback: !!query.error,
  }
}

/** Sidebar category/tag trees, falling back to the bundled demo content. */
export function useWebsiteBlogMeta() {
  const query = useQuery({
    queryKey: ['website', 'blog-meta'],
    queryFn: fetchWebsiteBlogMeta,
    staleTime: STALE_TIME,
  })

  const categories = query.data?.data?.categories?.length
    ? query.data.data.categories
    : blogCategoryTree
  const tagTree = query.data?.data?.tagTree?.length ? query.data.data.tagTree : blogTagTree
  const tags = query.data?.data?.tags?.length
    ? query.data.data.tags
    : tagTree.map((tag) => tag.name)

  return { categories, tagTree, tags, isLoading: query.isLoading, isFallback: !!query.error }
}

/**
 * Site-wide contact details (header, footer, offcanvas, /contact), falling back
 * to the bundled defaults when the API is unreachable.
 */
export function useWebsiteContact() {
  const query = useQuery({
    queryKey: ['website', 'contact'],
    queryFn: fetchWebsiteContact,
    staleTime: STALE_TIME,
  })

  const contact: WebsiteContactInfo = { ...defaultContact, ...(query.data?.data ?? {}) }

  return {
    contact,
    isLoading: query.isLoading,
    isFallback: !!query.error,
  }
}

/** Submits the public contact form. */
export function useSubmitContact() {
  return useMutation({
    mutationFn: (payload: ContactFormPayload) => submitContactForm(payload),
  })
}

/** Demo gallery assets used when the API is unreachable. */
const fallbackGalleryImages: GalleryAsset[] = galleryImages.map((image, index) => ({
  id: `demo-image-${index}`,
  type: 'image',
  title: '',
  image,
  videoUrl: '',
  displayOrder: index,
}))

const fallbackGalleryVideos: GalleryAsset[] = galleryVideos.map((video, index) => ({
  id: `demo-video-${index}`,
  type: 'video',
  title: '',
  image: video.thumbnail,
  videoUrl: video.url,
  displayOrder: index,
}))

/**
 * Active gallery assets, split into images and videos. Falls back to the
 * bundled demo content when the API is unreachable so the page never renders
 * empty.
 */
export function useWebsiteGallery() {
  const query = useQuery({
    queryKey: ['website', 'gallery'],
    queryFn: fetchWebsiteGallery,
    staleTime: STALE_TIME,
  })

  const images = query.data?.data?.images ?? fallbackGalleryImages
  const videos = query.data?.data?.videos ?? fallbackGalleryVideos

  return { images, videos, isLoading: query.isLoading, isFallback: !!query.error }
}

/**
 * Active testimonials, falling back to the bundled demo content when the API
 * is unreachable so the page never renders empty.
 */
export function useWebsiteTestimonials() {
  const query = useQuery({
    queryKey: ['website', 'testimonials'],
    queryFn: fetchWebsiteTestimonials,
    staleTime: STALE_TIME,
  })

  return {
    testimonials: query.data?.data ?? fallbackTestimonials,
    isLoading: query.isLoading,
    isFallback: !!query.error,
  }
}

/**
 * Active pricing plans shown in the home page "Plans & Pricing" section,
 * falling back to the bundled demo content when the API is unreachable.
 */
export function useWebsitePlans() {
  const query = useQuery({
    queryKey: ['website', 'plans'],
    queryFn: fetchWebsitePlans,
    staleTime: STALE_TIME,
  })

  return {
    plans: query.data?.data ?? fallbackPlans,
    isLoading: query.isLoading,
    isFallback: !!query.error,
  }
}

/**
 * Server-side public website mode. Defaults to `live` when the API is
 * unreachable so the real site keeps working, and never retries aggressively.
 */
export function useFrontendMode() {
  const query = useQuery({
    queryKey: ['website', 'frontend-mode'],
    queryFn: fetchFrontendMode,
    staleTime: STALE_TIME,
    retry: 1,
  })

  const mode = query.data?.data?.mode ?? 'live'

  return {
    mode,
    isDevelopment: mode === 'development',
    isLoading: query.isLoading,
  }
}

/** Hospital branding/details resolved from the public settings endpoint. */
export interface HospitalBranding {
  /** Uploaded hospital logo URL; empty string when not configured. */
  logo: string
  /** Uploaded browser favicon URL; empty string when not configured. */
  favicon: string
  name: string
  nameBn: string
  shortName: string
  tagline: string
  taglineBn: string
  address: string
  email: string
  website: string
  hotline: string
  certified: string
  /** Raw key/value settings map, for callers that need other public keys. */
  settings: PublicSettingsMap
  isLoading: boolean
  isFallback: boolean
}

/**
 * Public hospital settings (logo, name, contact) configured in the dashboard's
 * Settings → General page. Falls back to the bundled defaults when the API is
 * unreachable so the site never renders an empty brand.
 */
export function useHospitalSettings(): HospitalBranding {
  const query = useQuery({
    queryKey: ['website', 'public-settings'],
    queryFn: fetchPublicSettings,
    staleTime: STALE_TIME,
    retry: 1,
  })

  const settings: PublicSettingsMap = query.data?.data ?? {}
  const pick = (key: string, fallback = '') => (settings[key] ?? '').trim() || fallback

  return {
    logo: pick('hospital_logo'),
    favicon: pick('hospital_favicon'),
    name: pick('hospital_name_en', pick('hospital_name', site.name)),
    nameBn: pick('hospital_name_bn'),
    shortName: pick('hospital_short_name'),
    tagline: pick('hospital_tagline_en', site.tagline),
    taglineBn: pick('hospital_tagline_bn'),
    address: pick('hospital_address', site.address),
    email: pick('hospital_email', site.email),
    website: pick('hospital_website'),
    hotline: pick('hospital_hotline', site.phone),
    certified: pick('hospital_certified'),
    settings,
    isLoading: query.isLoading,
    isFallback: !!query.error,
  }
}

/**
 * Keeps the document favicon in sync with the dashboard's Settings → General
 * configuration. Prefers the dedicated favicon, falls back to the hospital logo,
 * and finally to the bundled default so the tab icon is never empty.
 */
export function useBrandFavicon() {
  const { favicon, logo } = useHospitalSettings()

  useEffect(() => {
    const href = favicon || logo || '/assets/images/favicon.png'
    const links = document.querySelectorAll<HTMLLinkElement>(
      'link[rel="icon"], link[rel="shortcut icon"]',
    )
    if (links.length > 0) {
      links.forEach((link) => link.setAttribute('href', href))
      return
    }
    const link = document.createElement('link')
    link.rel = 'icon'
    link.href = href
    document.head.appendChild(link)
  }, [favicon, logo])
}
